// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Anchor, Box, Loader, Paper, Stack, Text } from '@mantine/core';
import type { JSX } from 'react';
import { Link, useNavigate } from 'react-router';
import { PageHeader } from '../../../components/layout/PageHeader';
import { BookingFlow } from '../../../components/patient-appointments/BookingFlow';
import { usePatient } from '../../../hooks/usePatient';
import { useSchedulingEligibility } from '../../../hooks/useSchedulingEligibility';

/**
 * Provider-initiated scheduling page, nested under `/Patient/:patientId/Appointment/schedule`.
 *
 * Loads the patient from the enclosing route, checks their scheduling
 * eligibility, and renders `BookingFlow` configured to navigate back to the
 * Appointments tab on success or cancel. The route is gated by
 * `schedule-appointment` permission in App.tsx.
 * @returns The scheduling page component
 */
export function SchedulingPage(): JSX.Element {
  const patient = usePatient();
  const navigate = useNavigate();
  const { eligibility, loading, error } = useSchedulingEligibility(patient);

  const backToAppointments = (): void => {
    navigate(`/Patient/${patient?.id}/Appointment`);
  };

  const breadcrumb = (
    <Anchor component={Link} to={`/Patient/${patient?.id}/Appointment`} size="sm">
      Appointments &gt;
    </Anchor>
  );

  if (!patient || loading) {
    return (
      <Stack gap="lg" p="md">
        <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
          {breadcrumb}
          <PageHeader title="Scheduling" />
        </Paper>
        <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
          <Stack align="center" py="xl">
            <Loader />
            <Text c="dimmed">Loading scheduling…</Text>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  if (error || !eligibility?.canSchedule) {
    return (
      <Stack gap="lg" p="md">
        <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
          {breadcrumb}
          <PageHeader title="Scheduling" />
        </Paper>
        <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
          <Alert color={error ? 'red' : 'yellow'} variant="light">
            {error?.message ?? eligibility?.reason ?? 'This patient is not eligible to schedule at this time.'}
          </Alert>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" p="md">
      <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
        {breadcrumb}
        <PageHeader title="Scheduling" />
      </Paper>
      <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
        <Box>
          <BookingFlow
            patient={patient}
            eligibility={eligibility}
            onSuccess={backToAppointments}
            onCancel={backToAppointments}
          />
        </Box>
      </Paper>
    </Stack>
  );
}
